<template>
  <fragment>
    <ContactDetails
      v-if="status === 'pending' || status === 'won' || status === 'repwon'"
      :title="buyerName"
      :email="email"
      :telephone="telephone"
    />
    <template v-if="status === 'pending'">
      <b-button
        block
        @click.prevent
        v-b-toggle="'project-details-' + id"
        variant="light"
        class="mb-2"
      >
        <span class="when-opened">
          <b-icon-chevron-up></b-icon-chevron-up> {{ $t('buttons.show_less') }}
        </span>
        <span class="when-closed">
          <b-icon-plus-circle></b-icon-plus-circle>
          {{ $t('project_details.project_details') }}
        </span>
      </b-button>

      <b-collapse :id="'project-details-' + id" :visible="status === 'expired'">
        <ProjectDetails :id="projectId" :status="status" :location="location" />
      </b-collapse>
    </template>

    <b-card no-body>
      <div class="card-header">
        <b-icon-chat-right-text class="mr-2"></b-icon-chat-right-text>
        <router-link
          v-if="!standalone"
          :to="{ name: 'View lead', params: { id: id, method: 'edit' } }"
          >{{ $t('quote_form.my_quote') }}</router-link
        >
        <span v-if="standalone">{{ $t('quote_form.my_quote') }}</span>
      </div>

      <b-form
        action="#"
        @submit.prevent="submitProject"
        enctype="multipart/form-data"
      >
        <b-list-group flush>
          <b-alert show dismissible :variant="alertVariant" v-if="showAlert">
            <h3 class="alert-heading" v-if="alertTitle">{{ alertTitle }}</h3>
            <div v-html="alertMsg" />
          </b-alert>

          <b-list-group-item>
            <b-form-group
              label-cols="12"
              label-cols-sm="4"
              label-cols-lg="3"
              label-for="labourCost"
              :label="$t('quote_form.labour_cost') + ':'"
              :description="$t('quote_form.labour_cost_pre_rut_label')"
            >
              <b-input-group>
                <b-input
                  type="number"
                  id="labourCost"
                  v-model.number="model.labourCost"
                  @click="clearZeroValue($event)"
                  @change="sumTotal"
                  @keyup="
                    sumTotal();
                    validate();
                  "
                  :min="0"
                  :oninput="'validity.valid||(value=\'\');'"
                ></b-input>
                {{ currency | currencySymbol }}
              </b-input-group>
            </b-form-group>

            <b-form-group
              label-cols="12"
              label-cols-sm="4"
              label-cols-lg="3"
              label-for="materialCost"
              :label="$t('quote_form.material_cost') + ':'"
            >
              <b-input-group>
                <b-input
                  type="number"
                  id="materialCost"
                  v-model.number="model.materialCost"
                  @click="clearZeroValue($event)"
                  @change="sumTotal"
                  @keyup="
                    sumTotal();
                    validate();
                  "
                  :min="0"
                  :oninput="'validity.valid||(value=\'\');'"
                ></b-input>
                {{ currency | currencySymbol }}
              </b-input-group>
            </b-form-group>

            <b-form-group
              label-cols="12"
              label-cols-sm="4"
              label-cols-lg="3"
              label-for="otherCost"
              :label="$t('quote_form.other_cost') + ':'"
            >
              <b-input-group>
                <b-input
                  type="number"
                  id="otherCost"
                  v-model.number="model.otherCost"
                  @click="clearZeroValue($event)"
                  @change="sumTotal"
                  @keyup="
                    sumTotal();
                    validate();
                  "
                  :min="0"
                  :oninput="'validity.valid||(value=\'\');'"
                ></b-input>
                {{ currency | currencySymbol }}
              </b-input-group>

              <b-form-text>{{
                $t('quote_form.material_cost_description')
              }}</b-form-text>
            </b-form-group>

            <b-form-group
              label-cols="12"
              label-cols-sm="4"
              label-cols-lg="3"
              label-for="totalCost"
              :label="$t('quote_form.total_cost') + ':'"
              :state="model.totalCost >= this.getMinTotalCostVal()"
            >
              <b-input-group>
                <b-input
                  type="number"
                  id="totalCost"
                  v-model.number="model.totalCost"
                  @click="clearZeroValue($event)"
                  @change="clearCostFields"
                  @keyup="
                    clearCostFields();
                    validate();
                  "
                  :min="0"
                  :oninput="'validity.valid||(value=\'\');'"
                  required
                  :state="validateTotalCost"
                  aria-describedby="input-live-help input-live-feedback"
                ></b-input>
                {{ currency | currencySymbol }}

                <b-form-invalid-feedback id="input-live-feedback">
                  {{ $t('messages.actions.quote_sum_too_low') }}
                  <b-icon
                    icon="question-circle-fill"
                    font-scale="1.5"
                    @mouseover="showTotalCostTooltip"
                    @mouseleave="hideTotalCostTooltip(2000)"
                  />
                  <b-tooltip
                    v-if="!validateTotalCost"
                    placement="right"
                    fallback-placement="clockwise"
                    target="totalCost"
                    triggers="null"
                    :ref="'reportSumTooltip' + id"
                    ><b-link
                      href="#"
                      v-b-modal="
                        'modal_customer_service_report_total_cost' + id
                      "
                      @click="hideTotalCostTooltip"
                      >{{ $t('customer_service.contact_us') }}</b-link
                    >
                    {{
                      $t('messages.actions.quote_user_explanation_tooltip')
                    }}</b-tooltip
                  >
                </b-form-invalid-feedback>
              </b-input-group>

              <b-form-text v-if="!sumTotalCostVAT() > 0">
                {{ $t('quote_form.total_cost_ex_vat_label') }}
              </b-form-text>

              <b-form-text v-if="sumTotalCostVAT() > 0">
                <b-badge variant="primary">{{
                  sumTotalCostVAT() | currency(currency)
                }}</b-badge>
                {{ $t('quote_form.total_cost_vat_desc') }} ({{ vat }}%)
              </b-form-text>
            </b-form-group>

            <b-form-group
              label-cols="12"
              label-cols-sm="4"
              label-cols-lg="3"
              label-for="notification"
              :label="$t('quote_form.message_buyer') + ':'"
            >
              <b-input-group>
                <b-form-textarea
                  id="notification"
                  :maxlength="1000"
                  v-model="model.notification"
                  :placeholder="$t('quote_form.message_help_text')"
                  rows="4"
                ></b-form-textarea>
              </b-input-group>
            </b-form-group>

            <b-form-group label-cols="12" label-cols-sm="4" label-cols-lg="3">
              <b-button
                block
                @click.prevent
                v-b-toggle="'upload-' + id"
                variant="light"
                v-if="status === 'pending' || status === 'quoted'"
              >
                <span class="when-opened">
                  <b-icon-chevron-up class="mb-2"></b-icon-chevron-up>
                  <br />
                  {{ $t('buttons.show_less') }}
                </span>
                <span class="when-closed">
                  <b-icon-plus-circle class="mb-2"></b-icon-plus-circle>
                  <br />
                  {{ $t('quote_form.upload_file') }}
                </span>
              </b-button>
            </b-form-group>

            <b-collapse :id="'upload-' + id">
              <b-form-group
                label-cols="12"
                label-cols-sm="4"
                label-cols-lg="3"
                :label="$t('quote_form.upload_file') + ':'"
              >
                <b-form-file
                  accept="application/pdf"
                  v-model="file"
                  :placeholder="$t('quote_form.select_file')"
                  drop-placeholder="Drop file here..."
                  class="mb-2 mr-2"
                ></b-form-file>
                {{ $t('quote_form.selected_file') }}:
                {{ file ? file.name : '' }}
              </b-form-group>
            </b-collapse>

            <b-embed
              v-if="fileExist"
              type="iframe"
              :src="
                api_url +
                  '/getcurrentquotefile?provisionLeadId=' +
                  id +
                  '&token=' +
                  api_token
              "
            />
            <b-link
              v-if="fileExist"
              target="_blank"
              :href="
                api_url +
                  '/getcurrentquotefile?provisionLeadId=' +
                  id +
                  '&token=' +
                  api_token
              "
              >{{ $t('quote_form.download') }}
            </b-link>
          </b-list-group-item>
        </b-list-group>
        <b-card-footer
          v-if="
            status === 'pending' || status === 'quoted' || status === 'expired'
          "
        >
          <b-button variant="primary" type="submit" :disabled="submitting">
            {{ $t('buttons.save_quote') }}
          </b-button>

          <b-button-group class="controls">
            <b-button-group v-if="status === 'pending'">
              <span
                :id="'disabled-wrapper' + this.id"
                class="d-inline-block"
                tabindex="0"
                style="outline: 0"
              >
                <b-button
                  pill
                  :variant="extendable ? 'outline-info' : ''"
                  v-b-modal="'modal_extend_time' + id"
                  :disabled="!extendable || submitting"
                  :style="!extendable ? 'pointer-events: none;' : ''"
                >
                  <b-icon-clock-history></b-icon-clock-history>
                  {{ $t('buttons.extend_time') }}
                </b-button>
              </span>
              <b-tooltip
                :target="'disabled-wrapper' + this.id"
                v-if="!extendable"
                >{{ $t('messages.actions.extend_time_notice') }}</b-tooltip
              >
              <b-button
                pill
                variant="outline-danger"
                v-b-modal="'modal_complain' + id"
                :disabled="submitting || !complainable"
              >
                <b-icon-x-circle></b-icon-x-circle>
                {{ $t('buttons.complain') }}
              </b-button>
            </b-button-group>
            <!-- <b-button-group v-if="status === 'quoted'">
              <b-button
                pill
                variant="outline-success"
                v-b-modal="'modal_won' + id"
                :disabled="submitting"
              >
                <b-icon-check-circle></b-icon-check-circle>
                {{ $t('buttons.won') }}
              </b-button>
              <b-button
                pill
                variant="outline-danger"
                v-b-modal="'modal_lost' + id"
                :disabled="submitting"
              >
                <b-icon-x-circle></b-icon-x-circle>
                {{ $t('buttons.lost') }}
              </b-button>
            </b-button-group> -->
          </b-button-group>
          <div class="mt-2" v-if="status === 'pending'">
            <small class="text-muted">
              <i>{{
                $t(
                  `quote_form.disclaimer${
                    user.commissionOnly ? 'ComOnly' : ''
                  }`,
                  {
                    commission: provisionPercent,
                    days_left: daysLeft,
                    sum: this.$options.filters.currency(expireFee, currency),
                  },
                )
              }}</i>
            </small>
          </div>
        </b-card-footer>
      </b-form>

      <b-modal
        :id="'modal_customer_service_report_total_cost' + id"
        :title="$t('quote.quote_explanation_title')"
        ok-only
        ok-variant="secondary"
        :ok-title="$t('buttons.cancel')"
      >
        <p>{{ $t('quote.quote_explanation_message') }}</p>
        <ContactDetails
          title="customer_service.title"
          :email="
            $t('menu.support_email') +
              '?subject=' +
              $t('quote.quote_explanation_title') +
              ' ' +
              projectId +
              ' | ' +
              buyerName +
              '&body=' +
              $t('quote.quote_explanation_email')
          "
          emailBtnLabel="customer_service.email_btn_label"
        />
      </b-modal>

      <LeadConfirmModal
        :modalId="`modal_complain${id}`"
        :title="$t('buttons.complain')"
        :buyerName="buyerName"
        :location="location"
        :totalCost="model.totalCost"
        :currency="currency"
        :currentStatus="status"
        targetStatus="invalid"
        :provisionLeadId="id"
        @confirm="({ paymentPreference, declinedReason }) => reportCompliant()"
      />

      <LeadConfirmModal
        :modalId="`modal_extend_time${id}`"
        :title="$t('buttons.extend_time')"
        :body="$t('messages.actions.confirm_extend_time')"
        :buyerName="buyerName"
        :location="location"
        :totalCost="model.totalCost"
        :currency="currency"
        :currentStatus="status"
        targetStatus="pending"
        :actionButtonText="$t('buttons.extend_time')"
        actionButtonVariant="outline-info"
        actionButtonIcon="clock-history"
        @confirm="
          ({ paymentPreference, declinedReason }) => requestTimeExtension()
        "
      />

      <!-- <LeadConfirmModal
        :modalId="`modal_won${id}`"
        :title="$t('buttons.won')"
        :body="$t('messages.actions.confirm_msg_won')"
        :buyerName="buyerName"
        :location="location"
        :totalCost="model.totalCost"
        :currency="currency"
        :currentStatus="status"
        targetStatus="won"
        :provisionPercent="provisionPercent"
        :actionButtonText="$t('buttons.won')"
        actionButtonVariant="outline-success"
        actionButtonIcon="check-circle"
        @confirm="
          ({ paymentPreference, declinedReason }) =>
            reportWon(paymentPreference)
        "
      />

      <LeadConfirmModal
        :modalId="`modal_lost${id}`"
        :title="$t('buttons.lost')"
        :body="$t('messages.actions.confirm_msg_lost')"
        :buyerName="buyerName"
        :location="location"
        :totalCost="model.totalCost"
        :currency="currency"
        :currentStatus="status"
        targetStatus="lost"
        :actionButtonText="$t('buttons.lost')"
        actionButtonVariant="outline-danger"
        actionButtonIcon="x-circle"
        @confirm="({ paymentPreference, declinedReason }) => reportLost()"
      /> -->
    </b-card>
  </fragment>
</template>
<script>
import ProjectDetails from '@/components/ProjectDetails.vue';
import ContactDetails from '@/components/ContactDetails.vue';
import LeadConfirmModal from '@/components/LeadConfirmModal.vue';
import api from '../api/api';

export default {
  components: {
    ProjectDetails,
    ContactDetails,
    LeadConfirmModal,
  },
  props: {
    id: {
      type: Number,
    },
    projectId: {
      type: Number,
    },
    extendable: {
      type: Boolean,
    },
    vat: {
      type: Number,
      default: 0,
    },
    currency: {
      type: String,
    },
    daysLeft: {
      type: Number,
      default: 0,
    },
    provisionPercent: {
      type: Number,
      default: 0,
    },
    expireFee: {
      type: Number,
      default: 0,
    },
    status: {
      type: String,
    },
    standalone: {
      type: Boolean,
    },
    buyerName: {
      type: String,
    },
    location: {
      type: String,
    },
    email: {
      type: String,
    },
    telephone: {
      type: String,
    },
    complainable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      model: {
        provisionLeadId: this.id,
        labourCost: 0,
        materialCost: 0,
        otherCost: 0,
        totalCost: 0,
        notification: '',
      },
      file: null,
      fileExist: false,
      submitting: false,
      api_url: process.env.VUE_APP_API_URL,
      api_token: sessionStorage.getItem('api_token'),
      showAlert: false,
      alertVariant: 'success',
      alertTitle: '',
      alertMsg: '',
      validateTotalCost: null,
    };
  },
  mounted() {
    if (this.status !== 'pending' && this.status !== 'expired') {
      this.getCurrentQuote();
      this.checkProjectFileExist();
    }
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
  },
  methods: {
    reloadPage() {
      window && window.location.reload();
    },
    getCurrentQuote() {
      api
        .getCurrentQuote(this.id)
        .then((res) => (this.model = res.data))
        .catch((err) => err);
    },
    submitProject() {
      if (this.model.totalCost >= this.getMinTotalCostVal()) {
        this.$emit('submit');
        this.submitting = true;

        if (this.file) {
          const formData = new FormData();

          formData.append('registerquotedata', JSON.stringify(this.model));
          formData.append('file', this.file);

          api
            .registerQuoteAndFile(formData)
            .then(() => {
              this.showAlert = true;
              this.alertTitle = this.$t('messages.success_title');
              this.alertMsg = this.$t('messages.actions.quote_saved');
            })
            .catch(() => {
              this.showAlert = true;
              this.alertVariant = 'danger';
              this.alertTitle = this.$t('messages.error');
              this.alertMsg = this.$t('messages.actions.quote_submit_error');
            })
            .finally(() => ((this.submitting = false), this.reloadPage()));
        } else {
          const formData = JSON.stringify(this.model);

          if (this.status !== 'expired') {
            api
              .registerQuote(formData)
              .then(() => {
                this.showAlert = true;
                this.alertTitle = this.$t('messages.success_title');
                this.alertMsg = this.$t('messages.actions.quote_saved');
              })
              .catch(() => {
                this.showAlert = true;
                this.alertVariant = 'danger';
                this.alertTitle = this.$t('messages.error');
                this.alertMsg = this.$t('messages.actions.quote_submit_error');
              })
              .finally(() => ((this.submitting = false), this.reloadPage()));
          } else {
            api
              .revertExpiredLead(formData)
              .then(() => {
                if (!this.standalone) {
                  this.$router.push({
                    name: 'View lead',
                    params: { id: this.id, method: 'edit' },
                  });
                } else {
                  this.showAlert = true;
                  this.alertTitle = this.$t('messages.success_title');
                  this.alertMsg = this.$t('messages.actions.quote_saved');
                }
              })
              .catch(() => {
                this.showAlert = true;
                this.alertVariant = 'danger';
                this.alertTitle = this.$t('messages.error');
                this.alertMsg = this.$t('messages.actions.quote_submit_error');
              })
              .finally(() => ((this.submitting = false), this.reloadPage()));
          }
        }
      }
    },
    checkProjectFileExist() {
      api
        .getCurrentQuoteFilename(this.id)
        .then((res) =>
          res.data != 0 ? (this.fileExist = true) : (this.fileExist = false),
        )
        .catch((err) => err);
    },
    showTotalCostTooltip() {
      this.$refs['reportSumTooltip' + this.id].$emit('open');
    },
    hideTotalCostTooltip(timeout = 0) {
      setTimeout(
        () => this.$refs['reportSumTooltip' + this.id].$emit('close'),
        timeout,
      );
    },
    sumTotal() {
      const labourCost = parseInt(this.model.labourCost) || 0;
      const materialCost = parseInt(this.model.materialCost) || 0;
      const otherCost = parseInt(this.model.otherCost) || 0;

      return (this.model.totalCost = labourCost + materialCost + otherCost);
    },
    sumTotalCostVAT() {
      return Math.round(
        (parseInt(this.model.totalCost) || 0) +
          ((parseInt(this.model.totalCost) || 0) / 100) * this.vat,
      );
    },
    getMinTotalCostVal() {
      return Math.round(this.expireFee * 7);
    },
    clearCostFields() {
      this.model.labourCost = 0;
      this.model.materialCost = 0;
      this.model.otherCost = 0;
    },
    clearZeroValue(e) {
      //If cost fields contains a 0, remove it on click
      if (e.target.value === '0') {
        e.target.value = '';
      }
    },
    validate() {
      this.validateTotalCost =
        this.model.totalCost >= this.getMinTotalCostVal();
    },
    reportCompliant() {
      this.$emit('submit');
      this.submitting = true;
      this.$bvModal.hide('modal_complain' + this.id);

      this.showAlert = true;
      (this.alertTitle = this.$t('compliant_form.request_sent')),
        (this.alertMsg = this.$t('messages.actions.quote_compliant'));
    },
    reportWon(paymentPreference) {
      this.$emit('submit');
      this.submitting = true;
      this.$bvModal.hide('modal_won' + this.id);

      api
        .reportProvisionLeadWon(this.id, paymentPreference)
        .then((res) => {
          if (res.data && res.data.reason === 'CARD_PAYMENT_FAILURE') {
            if (confirm(this.$t('pages.payment_confirmation.payment_failed'))) {
              this.$router.push({
                name: 'Payment',
                params: { userId: this.user.userId },
              });
            }
          }
          this.showAlert = true;
          this.alertTitle = this.$t('messages.success');
          this.alertMsg = this.$t('messages.actions.quote_won');
        })
        .catch((err) => err)
        .finally(() => ((this.submitting = false), this.reloadPage()));
    },
    reportLost() {
      this.$emit('submit');
      this.submitting = true;
      this.$bvModal.hide('modal_lost' + this.id);

      api
        .reportProvisionLeadLost(this.id)
        .then(() => {
          this.showAlert = true;
          this.alertTitle = this.$t('messages.success');
          this.alertMsg = this.$t('messages.actions.quote_lost');
        })
        .catch((err) => err)
        .finally(() => ((this.submitting = false), this.reloadPage()));
    },
    requestTimeExtension() {
      this.$emit('submit');
      this.submitting = true;
      this.$bvModal.hide('modal_extend_time' + this.id);

      api
        .requestTimeExtension(this.id)
        .then(() => {
          this.showAlert = true;
          this.alertTitle = this.$t('messages.success');
          this.alertMsg = this.$t('messages.actions.quote_extend_time');
        })
        .catch((err) => err)
        .finally(() => ((this.submitting = false), this.reloadPage()));
    },
  },
};
</script>
<style lang="scss" src="../assets/scss/projectForm.scss" scoped />
